/**
 * Booking Validation
 * Centralized validation logic for booking data
 */

import { parseISO, isValid as isValidDate } from 'date-fns';
import { isValidEmail } from '../../../lib/api-shared-utils';
import { BookingData, VALID_BOOKEE_EMAILS, ValidBookeeEmail } from './bookingConstants';

/**
 * Type-safe validation function for bookee emails
 */
export function isValidBookeeEmail(email: string): email is ValidBookeeEmail {
  return VALID_BOOKEE_EMAILS.includes(email as ValidBookeeEmail);
}

/**
 * Validate booking data with strong type assertions
 */
export function validateBookingData(
  data: BookingData,
  eventType: string | null
): { isValid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  // Validate email format
  if (!data.email || data.email.trim() === '') {
    errors.email = 'Email address is required';
  } else if (!isValidEmail(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Validate name
  if (!data.name || data.name.trim() === '') {
    errors.name = 'Full name is required';
  }

  // Validate bookeeEmail (must be in allowed list) - skip if eventType is present
  if (!eventType && (!data.bookeeEmail || !isValidBookeeEmail(data.bookeeEmail))) {
    errors.bookeeEmail = 'Please select a valid team member';
  }

  // Validate selected slot
  if (!data.selectedSlot) {
    errors.slot = 'Please select a time slot';
  } else {
    // Validate slot dates are valid ISO strings
    try {
      const startDate = parseISO(data.selectedSlot.start);
      const endDate = parseISO(data.selectedSlot.end);

      if (!isValidDate(startDate)) {
        errors.slot = 'Invalid start time';
      } else if (!isValidDate(endDate)) {
        errors.slot = 'Invalid end time';
      } else if (endDate <= startDate) {
        errors.slot = 'End time must be after start time';
      }
    } catch (e) {
      errors.slot = 'Invalid time slot format';
    }
  }

  // Validate location for in-person meetings
  if (!data.isOnline && (!data.location || data.location.trim() === '')) {
    errors.location = 'Location is required for in-person meetings';
  }

  // Validate description (optional but ensure it's a string)
  if (data.description !== undefined && typeof data.description !== 'string') {
    errors.description = 'Description must be text';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}
